/* eslint-disable sort-keys-fix/sort-keys-fix */
import * as pathToRegexp from 'path-to-regexp';

export const authenticatedPaths = {
  ACCOUNT: '/account',
  ACCOUNT_INFO: '/account/info',
  ACCOUNT_PASSWORD: '/account/password',
  ACCOUNTS: '/accounts',
  ACCOUNT_DETAILS: '/accounts/:accountId',

  CHECKOUT_FAILURE: '/checkout-failure',
  CHECKOUT_ORDER: '/checkout/:id',
  CHECKOUT_SUCCESS: '/checkout/:id/success',

  ORDER_PAYMENT_RESULT: '/checkout/:id/payment-result',
  ORDER_PAYMENT_FAILURE: '/checkout/:id/payment-failure',
  ORDER_PAYMENT_PENDING: '/checkout/:id/payment-pending',

  LOOKBOOK: '/lookbook',
  LOOKBOOK_SINGLE: '/lookbook/:lookbookId/:wishlistId?',
  LOOKBOOK_SINGLE_PRODUCT: '/lookbook/:lookbookId/product/:productId',
  LOOKBOOK_CMS_PAGE: '/lookbook/:lookbookId/page/:pageSlug/:wishlistId?',
  LOOKBOOK_WISHLIST_PAGE: '/lookbook/wishlist/:lookbookId/:wishlistId?',
  LOOKBOOK_SINGLE_SETTINGS_GENERAL: '/lookbook/:lookbookId/settings/general',
  LOOKBOOK_SINGLE_SETTINGS_NAVIGATION: '/lookbook/:lookbookId/settings/navigation',
  LOOKBOOK_SINGLE_SETTINGS_CRITERIA: '/lookbook/:lookbookId/settings/criteria',

  ACCOUNT_REQUESTS: '/account-requests',
  ACCOUNT_REQUEST_DETAILS: '/account-requests/:accountRequestId',

  ORDERS: '/orders',
  ORDERSHEETS: '/ordersheets',

  PRODUCTS_ROOT: '/products',
  PRODUCTS_ORDER: '/products/:id',

  PAGE_ROOT: '/pages/:pageSlug',
  PAGE_ORDER: '/pages/:pageSlug/:id',
  PAGES_LIST: '/pages',

  CMS_EDITOR: '/cms/:pageId',
  CMS_PREVIEW: '/cms/:pageId/preview',

  HOME_ROOT: '/home',
  HOME: '/home/:id',
  ROOT: '/',

  SELECTIONS: '/selections',
  SELECTION_DETAILS_LEGACY: '/orders/:id',
  SELECTION_DETAILS: '/selection/:id',

  SETTINGS: '/settings',
  TRANSLATIONS: '/translations',
  SETTINGS_CUSTOMIZATION: '/settings/customization',
  SETTINGS_NAVIGATION: '/settings/navigation',
  SETTINGS_TRANSLATIONS: '/settings/translations',

  LINESHEETS: '/linesheets',
  TOOLS: '/tools',

  INVOICES: '/invoices',
  INVOICE_DETAILS: '/invoice/:invoiceId/:invoiceHash',
  INVOICE_PAYMENT_PENDING: '/invoice/:invoiceId/:invoiceHash/pending',
  INVOICE_PAYMENT_PROCESSING: '/invoice/:invoiceId/:invoiceHash/processing',
  INVOICE_PAYMENT_SUCCESS: '/invoice/:invoiceId/:invoiceHash/success',
} as const;

export const unAuthenticatedPaths = {
  LOGIN: '/login',
  PAGE_NOT_FOUND: '/404',
  PASSWORD_CHANGE: '/password/change',
  PASSWORD_FORGOT: '/password/forgot',
  LINK_EXPIRED: '/link-expired',
  WELCOME: '/welcome',
};

const SHOWROOM_SUPPORT_URL = 'https://support.centra.com/showroom';

export const externalLinks = {
  SUPPORT: SHOWROOM_SUPPORT_URL,
  SUPPORT_TRANSLATIONS: `${SHOWROOM_SUPPORT_URL}/translations-in-showroom`,
};

export const paths: typeof authenticatedPaths & typeof unAuthenticatedPaths = {
  ...authenticatedPaths,
  ...unAuthenticatedPaths,
};

/**
 * compiled paths are functions which accept object with parameters which correspond to path parameters
 * e.g. compiledPaths.ACCOUNT_DETAILS({ accountId: 5}) → /accounts/5
 */

type ExtractPathParams<Path extends string> =
  string extends Path ? Record<string, string>
  : Path extends `${string}:${infer Param}?/${infer Rest}` ? { [K in Param]?: string } & ExtractPathParams<`/${Rest}`>
  : Path extends `${string}:${infer Param}/${infer Rest}` ? { [K in Param]: string } & ExtractPathParams<`/${Rest}`>
  : Path extends `${string}:${infer Param}?` ? { [K in Param]?: string }
  : Path extends `${string}:${infer Param}` ? { [K in Param]: string }
  : // eslint-disable-next-line @typescript-eslint/ban-types
    {};

type CompiledPaths<P extends Record<string, string>> = {
  [K in keyof P]: pathToRegexp.PathFunction<ExtractPathParams<P[K]>>;
};

function createCompiledPaths<const P extends Record<string, string>>(params: P): CompiledPaths<P> {
  return (Object.keys(params) as (keyof P)[]).reduce((acc, key) => {
    return {
      ...acc,
      [key]: pathToRegexp.compile(params[key] as string),
    };
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  }, {} as CompiledPaths<P>);
}

export const compiledPaths = createCompiledPaths(paths);
